import { useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { PackagePlus, Plus, ScanBarcode } from 'lucide-react'

import type {
    ListNotesParams,
    NoteStatus,
    ProductLotInReason,
} from '@/lib/api/product-lot-api'
import { ClearFilterButton } from '@/components/common/clear-filter-button'
import { defineListFilters } from '@/components/common/filters/core'
import { selectFilter } from '@/components/common/filters/select-filter'
import {
    noteFromDateFilter,
    noteSearchFilter,
    noteStatusFilter,
    noteToDateFilter,
} from '@/components/common/filters/presets/note-filters'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import {
    NOTE_CARD_CODE_CLASS,
    NoteListCard,
} from '@/components/common/NoteListCard'
import { ProductLotNotePageHeader } from '@/components/product-lots/ProductLotNotePageHeader'
import { ProductLotStatusBadge } from '@/components/product-lots/ProductLotStatusBadge'
import {
    NOTE_LIST_TABLE_CLASS,
    NOTE_LIST_WRAP_CELL_CLASS,
    NoteProductSummaryCells,
    NoteProductSummaryList,
} from '@/components/product-lots/NoteProductSummary'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useIsMobile } from '@/hooks/use-is-mobile'
import {
    useNoteListSearchTracking,
    useNoteProductSummaryTracking,
} from '@/hooks/use-note-product-summary-tracking'
import {
    productLotKeys,
    useProductLotInNotesInfiniteQuery,
    useProductLotInNotesQuery,
} from '@/hooks/use-product-lot-query'
import { formatDateTimeVN } from '@/lib/format'
import {
    LIST_SEARCH_KEYS,
    useRememberListSearch,
} from '@/lib/list-search-store'
import { StockInNotePrintButton } from '@/components/warehouse/stock-in-note-print-button'
import { productLotInNoteToPrintData } from '@/lib/print-adapters/stock-in-note-adapters'
import { productLotApi } from '@/lib/api/product-lot-api'
import { warehouseNoteStatusLabel } from '@/components/warehouse/note-status-badge'
import { m } from '@/lib/paraglide/messages'

/**
 * Search params của màn. Trang chi tiết đọc lại qua `recallListSearch<Search>`
 * để nút Quay lại đưa người dùng về đúng danh sách kèm bộ lọc đang dùng.
 */
export type Search = {
    q?: string
    status?: NoteStatus
    reason?: ProductLotInReason
    from_date?: string
    to_date?: string
    page?: number
}

/** Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip. */
const FILTERS = defineListFilters({
    q: noteSearchFilter(),
    status: noteStatusFilter(),
    reason: selectFilter<ProductLotInReason | 'all'>({
        label: m.sl_note_filter_reason,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.sl_note_filter_all_reasons() },
            {
                value: 'from_packaging',
                label: m.pll_in_reason_from_packaging(),
            },
            {
                value: 'from_product_packaging',
                label: m.pll_in_reason_from_product_packaging(),
            },
            {
                value: 'from_customer_return',
                label: m.pll_in_reason_from_customer_return(),
            },
            { value: 'adjustment', label: m.pll_in_reason_adjustment() },
            {
                value: 'from_manufacturing_partner',
                label: m.pll_in_reason_from_manufacturing_partner(),
            },
            {
                value: 'from_tea_sifting',
                label: m.pll_in_reason_from_tea_sifting(),
            },
            {
                value: 'from_agent_return',
                label: m.pll_in_reason_from_agent_return(),
            },
        ],
    }),
    from_date: noteFromDateFilter(),
    to_date: noteToDateFilter(),
})

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/sales-warehouse/stock-in-notes/',
)({
    component: Page,
    validateSearch: FILTERS.validateSearch,
})

function Page() {
    const search = Route.useSearch()
    useRememberListSearch(LIST_SEARCH_KEYS.stockInNotes, search)
    const navigate = useNavigate({
        from: '/sales-warehouse/stock-in-notes/',
    })

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const apiFilters = useMemo(
        () => ({
            ...(values.q ? { search: values.q } : {}),
            ...(values.status !== 'all' ? { status: values.status } : {}),
            ...(values.reason !== 'all' ? { reason: values.reason } : {}),
            ...(values.from_date ? { date_from: values.from_date } : {}),
            ...(values.to_date ? { date_to: values.to_date } : {}),
        }),
        [
            values.q,
            values.status,
            values.reason,
            values.from_date,
            values.to_date,
        ],
    )

    const params = useMemo<ListNotesParams>(
        () => ({ page, per_page: 50, ...apiFilters }),
        [page, apiFilters],
    )

    const isMobile = useIsMobile()

    const { data, isLoading, isFetching, refetch } = useProductLotInNotesQuery(
        params,
        { enabled: !isMobile },
    )

    // Mobile: infinite (params bỏ page — hook tự quản)
    const infiniteParams = useMemo(
        () => ({ per_page: 50, ...apiFilters }),
        [apiFilters],
    )
    const infinite = useProductLotInNotesInfiniteQuery(infiniteParams, {
        enabled: isMobile,
    })
    const mobileNotes = useMemo(
        () => infinite.data?.pages.flatMap((p) => p.data ?? []) ?? [],
        [infinite.data],
    )

    const notes = data?.data ?? []
    useNoteProductSummaryTracking({
        kind: 'in',
        layout: 'table',
        notes,
        enabled: !isMobile,
    })
    useNoteProductSummaryTracking({
        kind: 'in',
        layout: 'cards',
        notes: mobileNotes,
        enabled: isMobile,
    })
    const total = data?.meta?.total ?? 0
    useNoteListSearchTracking({
        kind: 'in',
        layout: 'table',
        query: values.q,
        total,
        ready: !!data && !isFetching,
        withFilters: barProps.activeCount > 0,
        enabled: !isMobile,
    })
    useNoteListSearchTracking({
        kind: 'in',
        layout: 'cards',
        query: values.q,
        total: infinite.data?.pages[0]?.meta?.total ?? 0,
        ready: !!infinite.data && !infinite.isFetching,
        withFilters: barProps.activeCount > 0,
        enabled: isMobile,
    })

    // Ô tìm kiếm nằm ngoài sheet nên không vào `activeCount`; rỗng vì từ khoá
    // cũng là rỗng vì bộ lọc (R10).
    const filtered = barProps.activeCount > 0 || !!values.q

    // Rỗng vì chưa có dữ liệu khác hẳn rỗng vì bộ lọc quá hẹp — trường hợp thứ
    // hai phải cho người dùng lối thoát ngay tại chỗ (R10).
    const emptyState = (
        <div className="flex flex-col items-center gap-3 py-8 text-center">
            <span className="text-muted-foreground">
                {filtered ? m.list_empty_filtered() : m.sl_note_empty()}
            </span>
            {filtered ? (
                <ClearFilterButton active onClear={barProps.onClear} />
            ) : null}
        </div>
    )

    return (
        <div className="space-y-4 page-pad">
            <ProductLotNotePageHeader
                icon={<PackagePlus className="size-5 shrink-0" />}
                title={m.pll_pnk_title()}
                primaryActions={
                    <>
                        <Button asChild variant="outline">
                            <Link
                                to="/sales-warehouse/stock-in-notes/quick"
                                className="w-full"
                            >
                                <ScanBarcode className="size-4 mr-1" />
                                {m.warehouse_quick_stock_in()}
                            </Link>
                        </Button>
                        <Button asChild>
                            <Link
                                to="/sales-warehouse/stock-in-notes/new"
                                className="w-full"
                            >
                                <Plus className="size-4 mr-1" />
                                {m.pll_note_create_pnk()}
                            </Link>
                        </Button>
                    </>
                }
            />

            <ListFilterBar
                {...barProps}
                clearActive={filtered}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/* Mobile: card list + Xem thêm */}
            <div className="space-y-2 md:hidden">
                {infinite.isLoading ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.sl_lots_loading()}
                    </div>
                ) : mobileNotes.length === 0 ? (
                    emptyState
                ) : (
                    <>
                        {mobileNotes.map((n) => (
                            <NoteListCard
                                actions={<PrintButton id={n.id} />}
                                key={n.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/sales-warehouse/stock-in-notes/$id',
                                        params: { id: n.id },
                                    })
                                }
                                code={
                                    <Link
                                        to="/sales-warehouse/stock-in-notes/$id"
                                        params={{ id: n.id }}
                                        className={NOTE_CARD_CODE_CLASS}
                                    >
                                        {n.code}
                                    </Link>
                                }
                                statusBadge={
                                    <ProductLotStatusBadge status={n.status} />
                                }
                                reason={reasonLabel(n.reason)}
                                totalLots={n.items_count ?? 0}
                                summary={
                                    <NoteProductSummaryList
                                        summary={n.product_summary}
                                    />
                                }
                                createdBy={n.created_by_name ?? undefined}
                                createdAt={n.created_at}
                            />
                        ))}
                        {infinite.hasNextPage && (
                            <Button
                                variant="outline"
                                className="w-full h-11"
                                disabled={infinite.isFetchingNextPage}
                                onClick={() => void infinite.fetchNextPage()}
                            >
                                {infinite.isFetchingNextPage
                                    ? m.list_loading_more()
                                    : m.list_load_more()}
                            </Button>
                        )}
                    </>
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border bg-card overflow-x-auto">
                <Table className={NOTE_LIST_TABLE_CLASS}>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.sl_note_col_code()}</TableHead>
                            <TableHead>{m.sl_note_col_status()}</TableHead>
                            <TableHead>{m.sl_note_col_reason()}</TableHead>
                            <TableHead>{m.sl_note_col_product()}</TableHead>
                            <TableHead className="text-right">
                                {m.sl_note_col_quantity()}
                            </TableHead>
                            <TableHead className="text-center">
                                {m.sl_note_col_items()}
                            </TableHead>
                            <TableHead>{m.sl_note_col_created_by()}</TableHead>
                            <TableHead>{m.sl_note_col_created_at()}</TableHead>
                            <TableHead className="w-16 text-center">
                                {m.sinp_print_btn()}
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading ? (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    className="py-8 text-center text-muted-foreground"
                                >
                                    {m.sl_lots_loading()}
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9}>{emptyState}</TableCell>
                            </TableRow>
                        ) : (
                            notes.map((n) => (
                                <TableRow
                                    key={n.id}
                                    className="cursor-pointer hover:bg-muted/50"
                                    onClick={() =>
                                        void navigate({
                                            to: '/sales-warehouse/stock-in-notes/$id',
                                            params: { id: n.id },
                                        })
                                    }
                                >
                                    <TableCell className="font-mono text-sm">
                                        <Link
                                            to="/sales-warehouse/stock-in-notes/$id"
                                            params={{ id: n.id }}
                                            className="text-primary hover:underline"
                                        >
                                            {n.code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <ProductLotStatusBadge
                                            status={n.status}
                                        />
                                    </TableCell>
                                    <TableCell
                                        className={`${NOTE_LIST_WRAP_CELL_CLASS} min-w-28`}
                                    >
                                        {reasonLabel(n.reason)}
                                    </TableCell>
                                    <NoteProductSummaryCells
                                        summary={n.product_summary}
                                    />
                                    <TableCell className="text-center">
                                        {n.items_count ?? 0}
                                    </TableCell>
                                    <TableCell
                                        className={`${NOTE_LIST_WRAP_CELL_CLASS} min-w-24`}
                                    >
                                        {n.created_by_name ?? '—'}
                                    </TableCell>
                                    <TableCell
                                        className={`${NOTE_LIST_WRAP_CELL_CLASS} min-w-20 text-sm text-muted-foreground`}
                                    >
                                        {formatDateTimeVN(n.created_at)}
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <PrintButton id={n.id} />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {/* Pagination — desktop/tablet only */}
            <div className="hidden md:flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(Math.max(1, page - 1))}
                >
                    {m.sl_pagination_prev()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.sl_pagination_page({
                        page,
                        total: Math.max(1, Math.ceil(total / 50)),
                    })}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={!total || page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.sl_pagination_next()}
                </Button>
            </div>
        </div>
    )
}

export function reasonLabel(reason: ProductLotInReason | string): string {
    switch (reason) {
        case 'from_packaging':
            return m.pll_in_reason_from_packaging()
        case 'from_product_packaging':
            return m.pll_in_reason_from_product_packaging()
        case 'from_customer_return':
            return m.pll_in_reason_from_customer_return()
        case 'from_order_cancel':
            return m.pll_in_reason_from_order_cancel()
        case 'adjustment':
            return m.pll_in_reason_adjustment()
        case 'from_manufacturing_partner':
            return m.pll_in_reason_from_manufacturing_partner()
        case 'from_tea_sifting':
            return m.pll_in_reason_from_tea_sifting()
        case 'from_agent_return':
            return m.pll_in_reason_from_agent_return()
        case 'from_stocktake_surplus':
            return m.pl_reason_from_stocktake_surplus()
        default:
            return reason
    }
}

/** Nút in phiếu nhập kho cho 1 dòng danh sách — tải chi tiết phiếu rồi mới in
 *  vì API danh sách không trả dòng hàng. */
/** Nút in phiếu nhập kho cho 1 dòng danh sách — tải chi tiết phiếu rồi mới in
 *  vì API danh sách không trả dòng hàng. */
function PrintButton({ id }: { id: string }) {
    const qc = useQueryClient()
    return (
        <StockInNotePrintButton
            iconOnly
            askBagsForKg
            loadNote={async () =>
                (
                    await qc.fetchQuery({
                        queryKey: productLotKeys.inNoteDetail(id),
                        queryFn: () => productLotApi.getInNote(id),
                    })
                ).data ?? null
            }
            toPrintData={(note) =>
                productLotInNoteToPrintData(note, {
                    warehouseLabel: m.sinp_wh_sales(),
                    reasonLabel: reasonLabel(note.reason),
                    statusLabel: warehouseNoteStatusLabel(note.status),
                })
            }
        />
    )
}
